import Hls from "hls.js";

export type PlaybackMode = "native" | "hls.js" | "unsupported";

export interface PlayerAudioTrack {
  id: string;
  label: string;
  locale: string | null;
  selected: boolean;
}

export interface PlayerCallbacks {
  onFatalError(message: string): void;
  onAudioTracks(tracks: PlayerAudioTrack[]): void;
}

export interface PlayerHandle {
  destroy(): void;
  selectAudioTrack(id: string): void;
}

export function selectPlaybackMode(capabilities: { nativeHls: boolean; hlsJsSupported: boolean }): PlaybackMode {
  if (capabilities.nativeHls) return "native";
  if (capabilities.hlsJsSupported) return "hls.js";
  return "unsupported";
}

export function attachPlayer(video: HTMLVideoElement, manifestUrl: string, callbacks: PlayerCallbacks): PlayerHandle {
  const mode = selectPlaybackMode({
    nativeHls: video.canPlayType("application/vnd.apple.mpegurl") !== "",
    hlsJsSupported: Hls.isSupported(),
  });
  if (mode === "native") {
    const audioTracks = nativeAudioTrackList(video);
    const reportAudioTracks = (): void => callbacks.onAudioTracks(normalizeNativeAudioTracks(audioTracks));
    const onNativeError = (): void => callbacks.onFatalError("Không thể phát video (native)");
    video.src = manifestUrl;
    video.addEventListener("error", onNativeError);
    video.addEventListener("loadedmetadata", reportAudioTracks);
    for (const event of nativeAudioEvents) audioTracks?.addEventListener(event, reportAudioTracks);
    reportAudioTracks();
    return {
      destroy: () => {
        video.removeEventListener("error", onNativeError);
        video.removeEventListener("loadedmetadata", reportAudioTracks);
        for (const event of nativeAudioEvents) audioTracks?.removeEventListener(event, reportAudioTracks);
        video.pause();
        video.removeAttribute("src");
        video.load();
      },
      selectAudioTrack: id => selectNativeAudioTrack(audioTracks, id),
    };
  }
  if (mode === "hls.js") {
    const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
    const reportAudioTracks = (): void => callbacks.onAudioTracks(hls.audioTracks.map((track, index) => ({
      id: `hls:${index}`,
      label: track.name || track.lang || `Audio ${index + 1}`,
      locale: track.lang || localeLike(track.name),
      selected: hls.audioTrack === index,
    })));
    hls.loadSource(manifestUrl);
    hls.attachMedia(video);
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) callbacks.onFatalError(`Không thể phát video (${data.type})`);
    });
    hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, reportAudioTracks);
    hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, reportAudioTracks);
    callbacks.onAudioTracks([]);
    return {
      destroy: () => {
        hls.destroy();
        video.removeAttribute("src");
      },
      selectAudioTrack: id => {
        const match = /^hls:(\d+)$/.exec(id);
        const index = match?.[1] === undefined ? Number.NaN : Number(match[1]);
        if (Number.isInteger(index) && index >= 0 && index < hls.audioTracks.length) hls.audioTrack = index;
      },
    };
  }
  callbacks.onAudioTracks([]);
  callbacks.onFatalError("Trình duyệt này không hỗ trợ HLS.");
  return { destroy: () => undefined, selectAudioTrack: () => undefined };
}

const nativeAudioEvents = ["addtrack", "removetrack", "change"] as const;

interface NativeAudioTrack {
  id: string;
  label: string;
  language: string;
  enabled: boolean;
}

interface NativeAudioTrackList {
  readonly length: number;
  readonly [index: number]: NativeAudioTrack | undefined;
  item?(index: number): NativeAudioTrack | null;
  addEventListener(event: string, listener: () => void): void;
  removeEventListener(event: string, listener: () => void): void;
}

function nativeAudioTrackList(video: HTMLVideoElement): NativeAudioTrackList | undefined {
  return (video as HTMLVideoElement & { audioTracks?: NativeAudioTrackList }).audioTracks;
}

function normalizeNativeAudioTracks(audioTracks: NativeAudioTrackList | undefined): PlayerAudioTrack[] {
  if (!audioTracks) return [];
  return Array.from({ length: audioTracks.length }, (_, index) => nativeAudioTrackAt(audioTracks, index)).flatMap((track, index) => track ? [{
    id: `native:${track.id || index}`,
    label: track.label || track.language || `Audio ${index + 1}`,
    locale: track.language || localeLike(track.label),
    selected: track.enabled,
  }] : []);
}

function selectNativeAudioTrack(audioTracks: NativeAudioTrackList | undefined, id: string): void {
  if (!audioTracks || !id.startsWith("native:")) return;
  const requestedId = id.slice("native:".length);
  for (let index = 0; index < audioTracks.length; index += 1) {
    const track = nativeAudioTrackAt(audioTracks, index);
    if (track) track.enabled = (track.id || String(index)) === requestedId;
  }
}

function nativeAudioTrackAt(audioTracks: NativeAudioTrackList, index: number): NativeAudioTrack | null {
  return audioTracks.item?.(index) ?? audioTracks[index] ?? null;
}

function localeLike(value: string | undefined): string | null {
  if (!value) return null;
  return /^[a-z]{2,3}(?:-[a-z0-9]{2,8})+$/i.test(value) ? value : null;
}
